import { ReleaseNote } from '@src/main/modules/parameters/domain/release-notes/release-note.interface';
import { ReleaseNoteRepository } from '@src/main/modules/parameters/domain/release-notes/release-note.repository';
import {
  CreateReleaseNoteRequest,
  PatchReleaseNoteRequest,
} from '@src/main/modules/parameters/infrastructure/dto/createUpdateReleaseNoteRequest';

import {
  ReleaseNoteEntity,
  toReleaseNote,
} from '@src/main/modules/parameters/infrastructure/entity/release-note.entity';
import { api, TokenProvider } from '@src/main/shared/infrastructure/entity/axios';

const categoriesPath = '/release_note';

export const createReleaseNoteHttpRepository = (
  tokenProvider: TokenProvider,
): ReleaseNoteRepository => new ReleaseNoteHttpRepository(tokenProvider);

class ReleaseNoteHttpRepository implements ReleaseNoteRepository {
  private readonly tokenProvider: TokenProvider;

  constructor(tokenProvider: TokenProvider) {
    this.tokenProvider = tokenProvider;
  }

  private async getAuthToken(): Promise<string> {
    const token = await this.tokenProvider.getToken();
    if (!token) throw new Error('Authentication token is missing');
    return token;
  }

  async findAll(): Promise<ReleaseNote[]> {
    const token = await this.getAuthToken();
    const response = await api.get<ReleaseNoteEntity[]>(`${categoriesPath}/all`, token);
    return response.map(toReleaseNote);
  }

  async findById(id: string): Promise<ReleaseNote> {
    const token = await this.getAuthToken();
    const response = await api.get<ReleaseNoteEntity>(`${categoriesPath}/${id}`, token);
    return toReleaseNote(response);
  }

  async create(releaseNote: CreateReleaseNoteRequest): Promise<ReleaseNote> {
    const token = await this.getAuthToken();
    const response = await api.post<ReleaseNoteEntity>(categoriesPath, token, releaseNote);
    return toReleaseNote(response);
  }

  async update(id: string, releaseNote: PatchReleaseNoteRequest): Promise<ReleaseNote> {
    const token = await this.getAuthToken();
    const response = await api.patch<ReleaseNoteEntity>(
      `${categoriesPath}/${id}`,
      token,
      releaseNote,
    );
    return toReleaseNote(response);
  }

  async delete(id: string): Promise<void> {
    const token = await this.getAuthToken();
    await api.delete(`${categoriesPath}/${id}`, token);
  }
}
